import { Euro, Send, Package, Store, Truck, Calendar, Tag, CreditCard, HandCoins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

export default function QuoteBuilder({
  lines, setLine, fulfillment, setFulfillment, message, setMessage,
  onSubmit, saving, hasQuote, dr, partsTotal, deliveryFee, total,
  paymentRequired, setPaymentRequired,
}) {
  return (
    <form onSubmit={onSubmit} className="mt-6 overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-sm" data-testid="quote-builder">
      {/* Header */}
      <div className="flex flex-col gap-1 border-b border-slate-100 bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-5 text-white sm:px-8">
        <div className="flex items-center gap-2.5">
          <Euro className="h-6 w-6" />
          <h2 className="font-display text-xl font-bold">Préparer le devis</h2>
          <span className="ml-auto rounded-full bg-white/20 px-3 py-1 text-sm font-semibold">{lines.length} ligne{lines.length > 1 ? "s" : ""}</span>
        </div>
        <p className="text-sm text-blue-50">Renseignez le prix, la disponibilité, la référence et le stock de chaque pièce.</p>
      </div>

      <div className="space-y-5 px-6 py-6 sm:px-8">
        {/* Per-line cards */}
        {lines.map((l, i) => {
          const subtotal = (parseFloat(l.price) || 0) * (l.quantity || 1);
          return (
            <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/40 p-5 transition-colors hover:border-blue-300" data-testid={`quote-line-input-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
                <p className="flex items-center gap-2.5 font-display text-base font-semibold text-slate-900">
                  <span className="grid h-7 w-7 place-items-center rounded-md bg-blue-600 text-xs font-bold text-white">{l.quantity}×</span>
                  {l.name}
                </p>
                <span className="rounded-lg bg-white px-3 py-1 text-sm font-bold text-blue-700 ring-1 ring-blue-100" data-testid={`line-subtotal-${i}`}>
                  {subtotal.toFixed(2)} €
                </span>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <Label className="flex items-center gap-1 text-xs font-medium text-slate-600"><Tag className="h-3 w-3" /> Marque de pièce</Label>
                  <Input value={l.brand} onChange={(e) => setLine(i, { brand: e.target.value })}
                    data-testid={`line-brand-${i}`} className="mt-1.5 h-11" placeholder="ex : Bosch, Brembo, Valeo" />
                </div>
                <div>
                  <Label className="flex items-center gap-1 text-xs font-medium text-slate-600"><Tag className="h-3 w-3" /> Référence <span className="text-rose-500">*</span></Label>
                  <Input value={l.reference} onChange={(e) => setLine(i, { reference: e.target.value })} required
                    data-testid={`line-ref-${i}`} className="mt-1.5 h-11 font-mono" placeholder="N° pièce / OEM" />
                </div>
                <div>
                  <Label className="flex items-center gap-1 text-xs font-medium text-slate-600"><Truck className="h-3 w-3" /> Fournisseur <span className="text-rose-500">*</span></Label>
                  <Input value={l.supplier} onChange={(e) => setLine(i, { supplier: e.target.value })} required
                    data-testid={`line-supplier-${i}`} className="mt-1.5 h-11" placeholder="Nom du fournisseur" />
                </div>
                <div>
                  <Label className="flex items-center gap-1 text-xs font-medium text-slate-600"><Euro className="h-3 w-3" /> Prix unitaire (€) <span className="text-rose-500">*</span></Label>
                  <Input type="number" step="0.01" min="0" value={l.price} onChange={(e) => setLine(i, { price: e.target.value })} required
                    data-testid={`line-price-${i}`} className="mt-1.5 h-11 text-base font-semibold" placeholder="0.00" />
                </div>
                <div>
                  <Label className="flex items-center gap-1 text-xs font-medium text-slate-600"><Calendar className="h-3 w-3" /> Disponible le</Label>
                  <Input type="date" value={l.available_date} onChange={(e) => setLine(i, { available_date: e.target.value })}
                    data-testid={`line-date-${i}`} className="mt-1.5 h-11" />
                </div>
              </div>

              <label className="mt-4 inline-flex cursor-pointer items-center gap-2.5 rounded-lg bg-white px-3.5 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200">
                <Checkbox checked={l.in_stock} onCheckedChange={(c) => setLine(i, { in_stock: !!c })} data-testid={`line-stock-${i}`} />
                <Package className={`h-4 w-4 ${l.in_stock ? "text-emerald-600" : "text-amber-500"}`} />
                {l.in_stock ? "En stock" : "Sur commande"}
              </label>
            </div>
          );
        })}

        {/* Fulfillment */}
        <div className="rounded-xl border border-slate-200 p-5" data-testid="fulfillment-builder">
          <p className="text-sm font-semibold text-slate-800">
            Mode de remise
            {dr.method === "delivery" && <span className="ml-1.5 font-medium text-amber-600">(livraison demandée par le client)</span>}
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {[["pickup", "Retrait en magasin", Store], ["delivery", "Livraison", Truck]].map(([key, label, Icon]) => {
              const active = fulfillment.method === key;
              return (
                <button key={key} type="button" onClick={() => setFulfillment((f) => ({ ...f, method: key }))}
                  data-testid={`fulfillment-${key}-btn`}
                  className={`flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-colors ${active ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  <Icon className="h-4 w-4" /> {label}
                </button>
              );
            })}
          </div>
          {fulfillment.method === "delivery" && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-medium text-slate-600">Frais de livraison (€)</Label>
                <Input type="number" step="0.01" min="0" value={fulfillment.fee}
                  onChange={(e) => setFulfillment((f) => ({ ...f, fee: e.target.value }))}
                  data-testid="fulfillment-fee-input" className="mt-1.5 h-11" placeholder="0.00" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Délai estimé</Label>
                <Input value={fulfillment.eta}
                  onChange={(e) => setFulfillment((f) => ({ ...f, eta: e.target.value }))}
                  data-testid="fulfillment-eta-input" className="mt-1.5 h-11" placeholder="ex : sous 48 h" />
              </div>
            </div>
          )}
        </div>

        {/* Payment decision */}
        <div className="rounded-xl border border-slate-200 p-5" data-testid="payment-mode-builder">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            <CreditCard className="h-4 w-4 text-blue-600" /> Conditions de paiement
          </p>
          <p className="mt-0.5 text-xs text-slate-500">Choisissez si le client doit régler après validation ou s'il bénéficie d'un paiement à crédit.</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {[
              [true, "Paiement obligatoire", "Le client paie après validation", CreditCard],
              [false, "À crédit", "Paiement convenu (bon client)", HandCoins],
            ].map(([val, label, desc, Icon]) => {
              const active = paymentRequired === val;
              return (
                <button key={String(val)} type="button" onClick={() => setPaymentRequired(val)}
                  data-testid={`payment-mode-${val ? "required" : "credit"}-btn`}
                  className={`flex flex-col items-start gap-0.5 rounded-lg border-2 px-4 py-3 text-left transition-colors ${active ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <span className={`flex items-center gap-2 text-sm font-semibold ${active ? "text-blue-700" : "text-slate-700"}`}>
                    <Icon className="h-4 w-4" /> {label}
                  </span>
                  <span className="text-xs text-slate-500">{desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Totals */}
        <div className="ml-auto w-full max-w-sm space-y-1.5 rounded-xl bg-slate-900 px-5 py-4 text-white">
          {deliveryFee > 0 && (
            <>
              <div className="flex items-center justify-between text-sm text-slate-300"><span>Pièces</span><span>{partsTotal.toFixed(2)} €</span></div>
              <div className="flex items-center justify-between text-sm text-slate-300"><span>Livraison</span><span>{deliveryFee.toFixed(2)} €</span></div>
              <div className="my-1.5 h-px bg-white/10" />
            </>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-200">Total du devis</span>
            <span className="font-display text-2xl font-bold" data-testid="builder-total">{total.toFixed(2)} €</span>
          </div>
        </div>

        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="quote-message-input"
          placeholder="Message au client (optionnel) — ex : délai, précision sur les pièces…" />

        <Button type="submit" disabled={saving} data-testid="send-quote-btn" className="h-12 w-full bg-blue-600 text-base hover:bg-blue-700">
          <Send className="mr-2 h-5 w-5" /> {saving ? "Envoi..." : hasQuote ? "Renvoyer le devis au client" : "Envoyer le devis au client"}
        </Button>
      </div>
    </form>
  );
}
